/* eslint-disable @typescript-eslint/naming-convention */
import { NativeLocalCart } from "./LocalCart";
import { UserAddress } from "./Address";
import { MROCartUpdateCouponRequest } from "./MROAPITypes";

/**
 * Used to decide if a child cart in MRO is selected or unselected.
 */
export enum MROChildCartState {
    SELECTED = "SELECTED",
    UNSELECTED = "UNSELECTED",
}

/**
 * Used to determine the user selected cart selection mode from the local cart or prefetch response
 */
export enum MROCartSelectionMode {
    SINGLE = "SINGLE",
    ALL = "ALL",
    PARTIAL = "PARTIAL",
    DEFAULT = "",
}

/**
 * Used to display the tabs in the MRO cart.
 */
export interface MROTab {
    restaurantId: number;
    restaurantName: string;
    order_total: number;
    itemCount: number;
    selected: MROChildCartState;
    cartId?: number;
    originalCartId?: number | string;
}

export interface MROMeals {
    id: number | string;
    quantity: number;
}

/**
 * A single restaurant's slice of the MRO native local cart.
 * This is client side representation of a single restaurant's cart in MRO order.
 */
export interface MROChildLocalCart extends NativeLocalCart {
    created_at?: number;
    cart_state?: MROChildCartState;
    // Restaurant-scoped coupon(s) to re-apply on the next update (e.g. FLAT150).
    cart_update_coupon_request?: MROCartUpdateCouponRequest[];
    // Full address (with sla_options) this child cart is delivered to.
    selectedAddress?: UserAddress | null;
    mealIds?: MROMeals[];
    // backend sends this to portal in checkout response to maintain cart continuity state across requests
    // this is also backed up in the local cart as it is used to maintain cart continuity state across requests
    cart_continuity_state_attributes?: Record<string, string | number | boolean>;
}

/**
 * MRO native local cart exchanged with the app bridge.
 * This is client side representation of the MRO cart.
 */
export interface MRONativeLocalCart extends Pick<
    NativeLocalCart,
    // Order-wide fields reused verbatim from the BAU local cart.
    | "cart_id"
    | "originalCartId"
    | "cart_type"
    | "address_id"
    | "cartLatLong"
    | "userLiveLat"
    | "userLiveLng"
    | "tipDetails"
    | "selected_drop_off_instruction_ids"
    | "couponCode"
    | "isCouponValid"
    | "is_incognito_cart"
    | "address_nudge_check"
    | "cart_initialization_source"
    | "cart_category_type"
    | "cart_journey_ctx"
    | "flowContext"
    | "date"
> {
    // ---- MRO-specific (not present on NativeLocalCart) ----
    selected_rxids?: string[];
    child_carts?: MROChildLocalCart[];
    // Order-wide delivery address (parent header; child carts carry their own).
    selectedAddress?: UserAddress | null;
    // Order-wide alliance / payment coupon (restaurant coupons live on each child).
    cart_update_coupon_request?: MROCartUpdateCouponRequest[];
    // snake_case here; NativeLocalCart exposes the legacy camelCase `discountMessage`.
    discount_message?: string;
    discount_message_type?: string;
    first_time_user?: boolean;
    is_menu_to_cart_transition?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}
